// One shared drag controller for the whole UI. Native HTML5 drag-and-drop was unreliable across the
// overlay layers, so this is a custom pointer drag: press on a draggable element, move past a small
// threshold to lift a ghost that follows the cursor, release over a drop zone to fire its handler.
//
// Drop zones are plain DOM elements tagged with data-drop="<zoneId>" (and, for the hotbar, data-slot).
// Any component registers a handler for a zoneId; the handler receives the drag payload and the zone
// element it landed on. Elements registered via registerLift() are raised above modals while a drag is
// in flight so the hotbar at the bottom is reachable even under a full-screen storage backdrop.
import { chipCss } from './skin';

export interface DragPayload {
  source: string;        // 'inventory' | 'storage' | 'hotbar'
  id: string;            // item id (resource/deployable) or hotbar item id
  slot?: number;         // 1-based hotbar slot when source === 'hotbar'
  index?: number;        // 0-based grid slot when source === 'inventory' | 'storage' (which exact stack)
  n?: number;            // stack size being dragged (drop/toss amounts)
}

type DropHandler = (payload: DragPayload, zone: HTMLElement) => void;

const THRESHOLD = 5; // px of movement before a press becomes a drag

export class DragController {
  private cand: { payload: DragPayload; label: string; x: number; y: number } | null = null;
  private active: DragPayload | null = null;
  private ghost: HTMLDivElement | null = null;
  private readonly handlers = new Map<string, DropHandler>();
  private readonly liftEls: HTMLElement[] = [];

  constructor() {
    document.addEventListener('mousemove', (e) => this.onMove(e));
    document.addEventListener('mouseup', (e) => this.onUp(e));
  }

  onDrop(zoneId: string, handler: DropHandler): void {
    this.handlers.set(zoneId, handler);
  }

  registerLift(el: HTMLElement): void {
    this.liftEls.push(el);
  }

  get dragging(): boolean {
    return this.active !== null;
  }

  // Fired when a drag is RELEASED over no drop zone at all (e.g. over the game canvas in third person).
  // Gets the release coords so the handler can decide (the hotbar's forgiveness ring + fling use this).
  onMiss: ((payload: DragPayload, x: number, y: number) => void) | null = null;

  // Call from an element's LEFT-button mousedown to make it a drag source.
  beginCandidate(payload: DragPayload, label: string, e: MouseEvent): void {
    if (e.button !== 0) return;
    e.preventDefault();
    this.cand = { payload, label, x: e.clientX, y: e.clientY };
  }

  // Start a drag IMMEDIATELY (no movement threshold) -- the hotbar's hold-to-drag path.
  beginForced(payload: DragPayload, label: string, x: number, y: number): void {
    this.start({ payload, label });
    this.moveGhost(x, y);
  }

  private onMove(e: MouseEvent): void {
    if (this.active) { this.moveGhost(e.clientX, e.clientY); return; }
    if (!this.cand) return;
    if (Math.abs(e.clientX - this.cand.x) + Math.abs(e.clientY - this.cand.y) < THRESHOLD) return;
    this.start(this.cand);
    this.moveGhost(e.clientX, e.clientY);
  }

  private start(cand: { payload: DragPayload; label: string }): void {
    this.active = cand.payload;
    this.cand = null;
    for (const el of this.liftEls) { el.dataset.prevZ = el.style.zIndex; el.style.zIndex = '40'; }
    const g = document.createElement('div');
    g.style.cssText = [
      'position:fixed', 'z-index:60', 'pointer-events:none',
      chipCss('sm'), 'box-shadow:0 6px 20px rgba(0,0,0,0.5)',
      'transform:translate(-50%,-50%)',
    ].join(';');
    g.textContent = cand.label;
    document.body.appendChild(g);
    this.ghost = g;
  }

  private onUp(e: MouseEvent): void {
    if (e.button !== 0) return;
    if (this.active) {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      const zone = el?.closest('[data-drop]') as HTMLElement | null;
      const id = zone?.dataset.drop;
      if (id && zone) this.handlers.get(id)?.(this.active, zone);
      else this.onMiss?.(this.active, e.clientX, e.clientY);
      this.cancel();
    } else {
      this.cand = null;
    }
  }

  private moveGhost(x: number, y: number): void {
    if (this.ghost) { this.ghost.style.left = `${x}px`; this.ghost.style.top = `${y}px`; }
  }

  // Tear down any in-flight drag/candidate. Public so a panel closing mid-drag (where the matching
  // mouseup never arrives over a drop zone) can't leave a ghost stuck or the hotbar lifted.
  cancel(): void {
    this.active = null;
    this.cand = null;
    if (this.ghost) { this.ghost.remove(); this.ghost = null; }
    for (const el of this.liftEls) { el.style.zIndex = el.dataset.prevZ ?? ''; delete el.dataset.prevZ; }
  }
}
